const CLOUD_NAME = "dtmuifr1a";
const BRAND_COLOR = "10b981";
const SITE_URL = "https://www.usepinned.com";

function extractPublicId(imageUrl) {
  if (!imageUrl) return null;
  const match = imageUrl.match(/\/upload\/(?:v\d+\/)?(.+)\.\w+$/);
  return match ? match[1] : null;
}

function enc(text) {
  return encodeURIComponent(text)
    .replace(/'/g, "%27")
    .replace(/\(/g, "%28")
    .replace(/\)/g, "%29");
}

/**
 * Build a 1080x1920 story image URL via Cloudinary transformations.
 */
export function buildStoryImageUrl(post) {
  const publicId = extractPublicId(post.imageUrl);
  if (!publicId) return null;

  const location = (post.locationName || "").slice(0, 40);
  const username = post.user?.username || "";
  const category = post.category || "adventure";

  const subtitleParts = [];
  if (username) subtitleParts.push(`@${username}`);
  subtitleParts.push(category);
  if (post.worthIt === true) subtitleParts.push("Worth it");
  if (post.worthIt === false) subtitleParts.push("Skip it");
  if (post.wouldReturn === true) subtitleParts.push("Would return");
  const subtitle = subtitleParts.join(" · ").slice(0, 60);

  const transforms = [
    // Photo padded to 9:16 with dark background
    "c_pad,w_1080,h_1920,g_north,b_rgb:0a0a0a",
    // Dark overlay at bottom for text
    "l_text:Arial_1:x,b_black,o_50,w_1080,h_400/fl_layer_apply,g_south",
    // Location name
    ...(location
      ? [`l_text:Arial_56_bold:${enc(location)},co_white/fl_layer_apply,g_south_west,x_50,y_280`]
      : []),
    // Subtitle
    `l_text:Arial_30:${enc(subtitle)},co_rgb:dddddd/fl_layer_apply,g_south_west,x_50,y_220`,
    // Pinned badge
    `l_text:Arial_32_bold:Pinned,co_white,b_rgb:${BRAND_COLOR},bo_8px_solid_rgb:${BRAND_COLOR},o_90/fl_layer_apply,g_south,y_80`,
    // URL
    `l_text:Arial_22:${enc("usepinned.com")},co_rgb:888888/fl_layer_apply,g_south,y_35`,
  ];

  return `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/${transforms.join("/")}/${publicId}.jpg`;
}

/**
 * Share a post — tries native share with image file, falls back to URL share, then clipboard.
 * Returns "shared" | "copied" | "cancelled"
 */
export async function sharePost(post) {
  const postUrl = `${SITE_URL}/profile/${post.user?.username || "pinned"}/post/${post.id}`;
  const storyUrl = buildStoryImageUrl(post);
  const shareText = post.caption
    ? `${post.caption.slice(0, 100)} — ${post.locationName || "Pinned"}`
    : `Check out this spot on Pinned — ${post.locationName || ""}`;

  // Try native share with image file (mobile browsers, IG Stories, WhatsApp)
  if (storyUrl && navigator.canShare) {
    try {
      const response = await fetch(storyUrl);
      const blob = await response.blob();
      const file = new File([blob], "pinned-story.jpg", { type: "image/jpeg" });

      if (navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: post.locationName || "Pinned",
          text: shareText,
          url: postUrl,
        });
        return "shared";
      }
    } catch (err) {
      if (err.name === "AbortError") return "cancelled";
      // Fall through to URL share
    }
  }

  // Try native share without image (works on most mobile browsers)
  if (navigator.share) {
    try {
      await navigator.share({
        title: post.locationName || "Pinned",
        text: shareText,
        url: postUrl,
      });
      return "shared";
    } catch (err) {
      if (err.name === "AbortError") return "cancelled";
    }
  }

  // Fallback: copy URL to clipboard
  try {
    await navigator.clipboard.writeText(postUrl);
    return "copied";
  } catch {
    return "cancelled";
  }
}
